import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClockRotateLeft,
  faXmarkCircle,
  faSync,
  faFileImport,
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { usePermissions } from '@shared/hooks/usePermissions';
import { ShopsModel, SHOP_STATUS, CHANNELS_WITH_IMPORT, CHANNELS_WITH_FILE_IMPORT } from '../../models/Shops';
import { AmazonFbmImportButton, AmazonFbmShipmentExportButton, DropdownActions } from '../interaction';
import { ShopSettingsMobileModal, ShopSettingsMobileModalRef } from '../modals';
import { ImportOrderMobileModal, ImportOrderMobileModalRef } from '../modals';
import { ShopAvatar } from './ShopAvatar';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<ShopsModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  refetch?: () => void; // 🎯 Función para actualizar lista después de cambios
}

// 🎯 Componente de card individual para Shops - SRP
export const Card = ({ item: shop, className = '', refetch }: CardProps) => {
  const { t } = useTranslation('shopsModule');
  const { hasPermission } = usePermissions();
  const mobileSettingsModalRef = useRef<ShopSettingsMobileModalRef>(null);
  const importOrderModalRef = useRef<ImportOrderMobileModalRef>(null);

  // 🎯 Verificar permisos de tiendas
  // NOTA: shops.manage NO está documentado y no debe usarse (problema de backend)
  // NOTA: shops.configure NO se usa para editar tiendas, solo para configuración de shipping
  const canEditShops = hasPermission('shops.edit');
  const canActivateShops = hasPermission('shops.activate');
  const canDeleteShops = hasPermission('shops.delete');

  const handleOpenSettings = () => {
    // En mobile, abrir la modal específica
    mobileSettingsModalRef.current?.open(shop);
  };

  const handleOpenImportOrder = () => {
    // Abrir modal de importación de órdenes y pasar la tienda
    importOrderModalRef.current?.open(shop);
  };

  return (
    <div
      className={`p-4 border border-card-border rounded-lg bg-card-bg hover:bg-hover-press transition-colors mb-4 ${className}`}
    >
      <div className="space-y-3">
        {/* Top Row: Icon, Name, Menu */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShopAvatar shop={shop} size="sm" />
            <span className="text-sm font-medium text-title">{shop.name}</span>
          </div>
          <DropdownActions
            shopId={shop.id}
            shopStatus={shop.status}
            channelId={shop.channel_id}
            onToggleStatus={
              canActivateShops
                ? () => {
                    // TODO: Implementar lógica para cambiar estado
                    refetch?.();
                  }
                : undefined
            }
            onDelete={
              canDeleteShops
                ? () => {
                    // 🎯 La eliminación real se hace en DropdownActions
                    // Aquí solo actualizamos la lista después de la eliminación exitosa
                    refetch?.();
                  }
                : undefined
            }
            onOpenSettings={canEditShops ? handleOpenSettings : undefined}
          />
        </div>

        {/* Second Row: Status Badges and Export Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 w-full">
            <Badge
              variant="outline"
              className={`flex items-center gap-2 text-xs w-fit border-0 py-1 ${
                shop.status > 0 ? 'bg-card-border light:bg-bg-success' : 'bg-card-border light:bg-bg-error'
              }`}
            >
              <FontAwesomeIcon
                icon={shop.status > 0 ? faCheckCircle : faXmarkCircle}
                className={`w-3 h-3 ${shop.status > 0 ? 'text-success dark:text-bright-success' : 'text-error'}`}
              />
              <span className={`${shop.status > 0 ? 'text-success dark:text-text' : 'text-error  dark:text-text'}`}>
                {shop.status > 0 ? SHOP_STATUS[1] : SHOP_STATUS[0]}
              </span>
            </Badge>
            <div className="flex justify-end">
              {shop.last_synchronization ? (
                <Badge
                  variant="outline"
                  className="flex items-center gap-2 text-xs w-fit bg-card-border border-0 py-1 light:bg-bg-success"
                  title={new Date(shop.last_synchronization).toLocaleString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                >
                  <FontAwesomeIcon icon={faSync} className="w-3 h-3 text-success dark:text-bright-success" />
                  <span className="text-xs font-medium text-title light:text-success">{t('sync.synced')}</span>
                </Badge>
              ) : (
                <span className="text-sm"></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {CHANNELS_WITH_IMPORT.includes(shop.channel_id) && (
              <Button
                variant="outline"
                size="sm"
                title={t('modals.importOrder.buttonTitle')}
                onClick={handleOpenImportOrder}
              >
                <FontAwesomeIcon icon={faFileImport} className="!w-3.5 !h-3.5 text-icons" />
              </Button>
            )}
            {CHANNELS_WITH_FILE_IMPORT.includes(shop.channel_id) && (
              <>
                <AmazonFbmImportButton shopId={shop.id} onImportSuccess={refetch} />
                <AmazonFbmShipmentExportButton shopId={shop.id} onExportSuccess={refetch} />
              </>
            )}
            {CHANNELS_WITH_IMPORT.includes(shop.channel_id) && (
              <Button asChild variant="outline" size="sm" title={t('buttons.history', 'Historial')}>
                <Link to={`/shops/import-history/${shop.id}`}>
                  <FontAwesomeIcon icon={faClockRotateLeft} className="!w-3.5 !h-3.5 text-icons" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Cliente (team) condicional */}
      {shop.team && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-sm text-complementary-text">{t('table.client')}:</span>
          <span className="text-sm font-medium">{shop.team.name}</span>
        </div>
      )}

      {/* Modal de configuración para mobile */}
      <ShopSettingsMobileModal ref={mobileSettingsModalRef} />
      <ImportOrderMobileModal ref={importOrderModalRef} />
    </div>
  );
};
